export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQCategory {
  title: string;
  items: FAQItem[];
}

export const FAQ_DATA: FAQCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'What is Drahft?',
        answer: `Drahft is a personalized AI assistant trained on your own business content. It understands your services, your process, and the way you communicate, so it can support your couples with clarity and consistency.

Here is what it helps with:
- Answering client questions at any time with accurate, friendly information
- Guiding new inquiries and helping you connect with the right couples
- Providing quick responses to your most common questions
- Creating bookings directly through conversations
- Drafting professional follow-up emails based on chat history
- Integrating smoothly into your website or messaging platform

You can think of it as a helpful extension of your brand, offering couples a welcoming and reliable experience from the moment they land on your site.`,
      },
      {
        question: 'How quickly can I get started?',
        answer: `You can have a fully functional AI Agent live on your website in under 5 minutes! Here is a quick overview:

1. Sign up and choose a plan (or start free with Hobby)
2. Add your training data: tell the AI about your business, services, and how you communicate
3. Copy your widget embed code from the Widget Settings page
4. Paste the code into your website (works with WordPress, Wix, Squarespace, Shopify, and 40+ platforms)
5. That's it! Your AI assistant is live and ready to help visitors

You can always refine your training data later to improve the quality of responses.`,
      },
      {
        question: 'Do I need technical skills to use Drahft?',
        answer: `Not at all. Drahft is designed so anyone can set it up with ease.
- No coding required, just a simple copy and paste to embed the widget
- Easy upload tools for your website content, guides, and documents
- A clean visual interface for all your settings
- Widget customization with live preview (colors, avatar, greeting messages)

If you do have a developer on your team, they can take things further with our optional API, but it's absolutely not required.`,
      },
      {
        question: 'Which website platforms does Drahft work with?',
        answer: `Drahft works with virtually any website. We provide step-by-step installation guides for over 40 platforms, including:
- WordPress (with a free plugin for easy install)
- Wix
- Squarespace
- Shopify
- Webflow
- Showit
- GoDaddy
- Google Sites
- Canva Websites
- Pixieset, Pic-Time, SmugMug, Zenfolio (popular for photographers)
- Google Tag Manager (GTM)
- Any custom HTML website

Visit the Integrations page in your dashboard for platform-specific guides. If your platform isn't listed, the Universal HTML method works with almost any site.`,
      },
    ],
  },
  {
    title: 'Features & Capabilities',
    items: [
      {
        question: 'What languages does Drahft support?',
        answer: `Drahft understands more than 95 languages and can automatically adjust to the language your clients use.

Here is how it works:
- It detects the language of each message and responds in the same one
- You can train it with content in one language and still receive answers in another
- It supports a wide range of popular languages including English, French, Spanish, German, Portuguese, Italian, Dutch, Russian, Chinese, Japanese, Korean, Arabic, and many others

This makes Drahft a great fit for businesses that serve couples from different backgrounds or regions, ensuring everyone feels welcomed and understood.`,
      },
      {
        question: 'How can I train my AI Agent?',
        answer: `Training your AI Agent is designed to be very simple. Everything happens inside your dashboard, where you'll find the AI Training Data section. Each field guides you through what to add, and you don't need any technical experience to complete it.

Just fill in the details about your services, your process, and the way you communicate. The clearer your information is, the more confidently Drahft can support your couples.

Each plan has a training data limit:
- Hobby: Up to 1,000 characters (short bio)
- Essentials: Up to 5,000 characters (detailed FAQ)
- Professional: Up to 20,000 characters (full business documentation)

A few minutes of setup gives your assistant everything it needs to sound like you and answer questions accurately.`,
      },
      {
        question: 'What are AI Email Drafts?',
        answer: `AI Email Drafts let you generate professional follow-up emails based on your conversation history. Instead of typing out every response from scratch, Drahft can draft a reply for you in seconds.

Each plan includes a monthly quota:
- Hobby: 10 email drafts/month
- Essentials: 100 email drafts/month
- Professional: 500 email drafts/month

Email drafts are generated from the Conversations tab. Just open a conversation and click the email draft button to get a polished response you can review and send.`,
      },
      {
        question: 'What is Advanced Prompt Customization?',
        answer: `Advanced Prompt Customization is not part of the current plan lineup.

Drahft now uses optimized defaults so your assistant can respond consistently out of the box. To influence responses, use your training data (FAQ entries, brand tone notes, and service details), which the AI uses as context when generating replies.`,
      },
      {
        question: 'Do I get notified when someone messages me?',
        answer: `Yes. All conversations are saved in your Conversations tab, where you can view your full chat history at any time. You can also receive email notifications when someone messages you, just make sure your admin notification settings are enabled.`,
      },
      {
        question: 'What are Conversation Ratings?',
        answer: `Conversation Ratings let your clients rate their chat experience after a conversation. This feature is available on all plans, including Hobby.

Ratings help you:
- Understand how well your AI assistant is performing
- Identify areas where training data could be improved
- Track client satisfaction over time
- Make data-driven improvements to your assistant's responses

You can view ratings in your Conversations tab. Every piece of feedback helps make your AI assistant better at serving your clients.`,
      },
      {
        question: 'How does Conversation History work?',
        answer: `Conversation History stores all chat interactions between your AI assistant and your clients. Each plan offers a different retention period:
- Hobby: 7 days
- Essentials: 90 days
- Professional: 12 months rolling

"Rolling" retention means your history continuously keeps the last 12 or 24 months of data. Older conversations beyond the retention window are automatically archived.

You can search, filter, and review conversations at any time from the Conversations tab in your dashboard.`,
      },
      {
        question: 'What is Widget Customization?',
        answer: `Widget Customization (available on Essentials and above) lets you personalize the look and feel of your chat widget to match your brand. You can change:
- Header and message colors
- Assistant name and avatar
- Greeting message and chat icon
- Widget position on the page
- Pre-message text and delay timing
- Custom CSS for advanced styling

Hobby plan users get a clean default widget that works great out of the box. Paid plans unlock the full customization panel with live preview.`,
      },
    ],
  },
  {
    title: 'Templates & Pipeline Automation',
    items: [
      {
        question: 'What are templates and how do they connect to my pipeline?',
        answer: `Templates are pre-written emails and messages that automatically send (or draft for your approval) when something happens in your pipeline, like a new inquiry arriving, a consult being booked, or a wedding being completed.

Think of templates as modular building blocks. You write them once inside AI Training Data, and Drahft calls them automatically at the right moment. This saves you hours every week and keeps your client communication consistent without losing your personal voice.

Two things make a template work:
1. A pipeline event slug like {inquiry_received} tells Drahft WHEN to fire the template.
2. Variables like {{client_name}} tell Drahft WHAT to personalize inside the template.

That's it. No coding, no complicated rules, just brackets.`,
      },
      {
        question: 'What is a pipeline event and how do I use the {curly_brackets}?',
        answer: `A pipeline event is a moment in your client journey where a template should fire. You attach an event to a template by typing its slug in single curly brackets anywhere in the template name, like this:

- "Auto reply {inquiry_received}"
- "Pricing guide {pricing_sent}"
- "Consult prep {consult_booked}"

When that event happens in your pipeline, Drahft automatically finds every template tagged with that slug and uses it. You can have multiple templates for the same event (for example, one email and one SMS).

The single bracket syntax { } is used ONLY for pipeline events. Keep it simple: one event per template.`,
      },
      {
        question: 'Which pipeline events can I hook into?',
        answer: `Here is the current event vocabulary you can use. Paste the slug (including the curly brackets) into your template name:

Lead stage:
- {inquiry_received} - Fires when a new contact is created from the widget or email
- {pricing_sent} - Fires when you mark a project as quoted or send pricing

Consultation stage:
- {consult_booked} - Fires when a booking is approved and confirmed
- {consult_reminder} - Fires 24 hours before a confirmed consult (automated)

Proposal stage:
- {proposal_sent} - Fires when you send an invoice or contract
- {proposal_followup} - Fires when an invoice is unpaid after a set number of days
- {booking_confirmed} - Fires when the contract is signed and deposit paid

Event stage:
- {wedding_reminder} - Fires 7 days before the wedding date (automated)
- {wedding_completed} - Fires the day after the wedding date

Delivery stage:
- {gallery_delivered} - Fires when you mark the project as delivered
- {review_request} - Fires 3 days after gallery delivery (automated)

More events will be added over time. You can always leave the event blank if you just want a manual template to pick from.`,
      },
      {
        question: 'What are variables and how do I use the {{double_brackets}}?',
        answer: `Variables let Drahft personalize every template automatically. You write them with double curly brackets so they do not get confused with pipeline events.

Example template body:
"Hello {{client_name}}, thanks for your {{event_type}} inquiry on {{event_date}}! I would love to chat soon."

When the template fires, Drahft replaces each variable with real client data. If a value is missing, the variable is skipped gracefully.

Currently supported variables:
- {{client_name}} - The lead's first name
- {{client_full_name}} - The lead's full name
- {{client_email}} - The lead's email address
- {{client_phone}} - The lead's phone number
- {{vendor_name}} - Your business name
- {{vendor_email}} - Your business email address
- {{business_name}} - Alias for your business name
- {{event_type}} - Wedding, engagement, elopement, etc.
- {{event_date}} - The event date, formatted nicely when parseable
- {{event_location}} - Venue or location
- {{consult_date}} - The scheduled consult date and time
- {{package_name}} - The package the client asked about
- {{price_total}} - Total quoted or invoiced amount (currency formatted)
- {{invoice_link}} - Direct link to the invoice when available
- {{booking.start_time}} - Booking start time
- {{booking.end_time}} - Booking end time
- {{invoice.due_date}} - Invoice due date
- {{invoice.balance}} - Outstanding invoice balance
- {{invoice.retainer_amount}} - Invoice retainer amount
- {{invoice.discounts}} - Applied discounts on the invoice
- {{invoice.payment_plan}} - Payment plan summary for the invoice
- {{client_portal}} - Link to the client's portal for this project

You can always preview the rendered template before it sends.`,
      },
      {
        question: 'Will Drahft rewrite my templates with AI?',
        answer: `Only if you ask it to. By default every template sends exactly as you wrote it, with variables swapped in. This keeps your voice consistent and costs nothing in AI credits.

Each template has an optional "AI Polish" toggle. When enabled, Drahft uses the conversation context to lightly personalize the template before sending (for example, mirroring the client's enthusiasm or adapting the tone). This is the only time AI credits are used for templates.

Recommended setup:
- Leave AI Polish OFF for transactional templates (confirmations, reminders, delivery notifications)
- Turn AI Polish ON for first-touch templates (inquiry auto-reply) where personalization has the biggest impact

This keeps your costs low while giving you AI magic exactly where it matters.`,
      },
      {
        question: 'Quick cheat sheet - brackets at a glance',
        answer: `Two bracket styles, two completely different jobs:

{ single_brackets } = Pipeline event slug. Tells Drahft WHEN to fire this template. One per template. Goes in the template NAME.

{{ double_brackets }} = Variable placeholder. Tells Drahft WHAT to personalize. Use as many as you want. Goes in the template BODY and subject.

Example template:
  Name:    Inquiry auto reply {inquiry_received}
  Subject: Thanks for reaching out, {{client_name}}!
  Body:    Hi {{client_name}}, I am so excited to hear about your {{event_type}} on {{event_date}}. Let me grab our pricing guide and get back to you shortly.

That is the whole system.`,
      },
    ],
  },
  {
    title: 'Bookings & Calendar',
    items: [
      {
        question: 'How do bookings work in Drahft?',
        answer: `Drahft's AI assistant can create bookings directly during conversations with your clients. When a potential client expresses interest in scheduling a consultation, meeting, or session, the AI will collect the necessary details and create the booking for you.

Here is how the booking flow works:
1. A visitor chats with your AI assistant on your website
2. They express interest in booking (e.g., "I'd like to schedule a consultation")
3. The AI asks for their preferred date, time, and contact information
4. The booking is created and saved to your dashboard
5. You can approve, reschedule, or decline bookings from the Bookings page

Each plan includes a monthly booking limit:
- Hobby: 5 bookings/month
- Essentials: 15 bookings/month
- Professional: Unlimited bookings

All bookings are visible in your Bookings dashboard regardless of your plan.`,
      },
      {
        question: 'What is the difference between basic bookings and Real-time AI Booking?',
        answer: `Great question! There are two levels of booking capability:

Standard Bookings (all plans):
- AI creates bookings based on the conversation
- Bookings are saved to your Drahft dashboard
- You manually review and manage them
- Google Calendar can still be connected for sync

Real-time AI Booking (Essentials and Professional):
- Everything above, plus Google Calendar integration
- AI checks your real-time calendar availability before suggesting times
- Prevents double-bookings automatically
- Approved bookings sync directly to your Google Calendar
- The AI respects your working hours and service durations

In short, basic bookings are like taking appointment requests. Real-time AI Booking is like having a smart scheduling assistant that knows your actual availability.`,
      },
      {
        question: 'How does Google Calendar integration work?',
        answer: `Google Calendar integration is available on all plans. It creates a two-way sync between Drahft and your Google Calendar.

Here is what it does:
- Reads your calendar to know when you are available or busy
- Syncs approved bookings to your Google Calendar automatically
- Respects your working hours and time zone settings
- Works with your existing calendar events (so appointments elsewhere block time correctly)

To connect: Go to Integrations in your dashboard, click Google Calendar, and authorize the connection. It takes about 30 seconds.

Note: Real-time availability checks during booking are included on Essentials and Professional.`,
      },
      {
        question: 'What is the Booking Planner?',
        answer: `Booking Planner is no longer a separate plan feature.

You can manage bookings directly from your Bookings page, use Google Calendar sync on all plans, and use Real-time AI Booking on Essentials and Professional for smart availability-aware scheduling.`,
      },
      {
        question: 'What happens when a booking is created?',
        answer: `When a client books through your AI assistant, here is what happens:

1. The booking is created in your Drahft dashboard
2. You will see it in your Bookings page
3. If Google Calendar is connected, the booking can sync automatically
4. You can approve, reschedule, or decline the booking
5. Approved bookings sync to your Google Calendar (if connected)
6. The client receives confirmation based on your settings

All bookings include the client's name, email, preferred time, service type, and any notes from the conversation.`,
      },
    ],
  },
  {
    title: 'Pricing & Credits',
    items: [
      {
        question: 'What plans are available?',
        answer: `Drahft offers three plans to fit every stage of business:

Hobby (Free):
- 50 AI messages/month, 10 email drafts, 5 bookings/month
- 7-day conversation history
- Perfect for testing the waters

Essentials ($19/month or $180/year):
- 500 AI messages/month, 100 email drafts, 15 bookings/month
- 90-day conversation history
- Unlimited projects, invoicing, forms, and contacts
- 5 workflows and Real-time AI Booking

Professional ($39/month or $374/year):
- Unlimited AI messages, 500 email drafts, unlimited bookings
- 12-month rolling history
- Google Calendar, Real-time AI Booking, unlimited workflows
- Google Analytics and expanded team collaboration

Yearly billing saves 20% on all paid plans. You can see the full comparison on the Pricing page.`,
      },
      {
        question: 'How do message credits work?',
        answer: `Message credits power the conversations your AI Agent has with your clients. Each time Drahft sends a response, it uses a small number of credits.

Your monthly allocation depends on your plan:
- Hobby: 50 messages/month
- Essentials: 500 messages/month
- Professional: Unlimited (fair-use rate limiting applies)

You can monitor your usage directly in your dashboard, where your token activity is displayed in real time. This makes it easy to keep track of how many credits you have used and how many remain.

"Unlimited" plans are subject to fair-use rate limiting (requests per minute) to ensure quality service for everyone.`,
      },
      {
        question: 'When do my credits reset?',
        answer: `Your message credits and email draft quotas refresh on the first day of every month. This happens automatically, no matter which date you signed up.

For example, if you join on the 12th of any month, your credits will reset on the 1st of the following month. Booking limits also reset monthly.`,
      },
      {
        question: 'What happens if I run out of credits?',
        answer: `If you run out of message credits, your AI Agent will temporarily pause and let visitors know it is unavailable. You will also receive a notification when your credits are running low or have been fully used, so you are never caught off guard.

You have a few options:
- Wait for credits to reset on the 1st of the next month
- Purchase a credit add-on (500, 1,000, or 1,500 credits)
- Upgrade to a higher plan for more monthly credits

Once credits are topped up, your assistant will resume helping your clients right away.`,
      },
      {
        question: 'Does adding a lot of AI Training Data cost more?',
        answer: `Usually, no. Saving more information in AI Training Data does not mean Drahft sends everything every time someone chats with you.

Here is the simple version:
- Your training data is stored so your assistant can learn your business, tone, packages, and process
- When Drahft creates a reply, it usually pulls only the most relevant parts
- That means a larger knowledge base does not automatically create a large cost on every message

The best approach is still to keep your training data clear and useful. A well-organized, practical set of answers works better than a huge wall of text.

You can now see this usage in your dashboard under Limits & Usage, including an estimate of how much AI Training Data was actually used.`,
      },
      {
        question: 'What is AI Training Data input usage?',
        answer: `AI Training Data input usage shows how much of your saved business information was actually used to help generate replies.

In plain language:
- It is not charging you for every word you save
- It tracks the parts of your saved training data that were actually used in AI responses
- This helps you understand how personalization affects usage over time

If you ever reach your plan's AI Training Data budget, Drahft will keep responding, but it may use less of your saved personalization until your monthly usage resets or you upgrade. Your assistant will still work, it just may sound a little less tailored until more usage is available.`,
      },
      {
        question: 'What are credit add-ons?',
        answer: `Credit add-ons are one-time purchases that give you extra AI message credits on top of your monthly plan. They are perfect for busy seasons when you need more capacity.

Available credit add-ons:
- 500 AI Message Credits: $9 one-time
- 1,000 AI Message Credits: $15 one-time
- Credit Bundle (1,500 credits): $20 one-time (Save $4 compared to buying separately)

Purchased credits are added to your account immediately and do not expire at the end of the month. They carry over until used.

You can buy add-ons from the Billing page in your dashboard.`,
      },
      {
        question: 'What other add-ons are available?',
        answer: `In addition to credit add-ons, Drahft offers a recurring subscription add-on:

Remove "Powered By Drahft" ($39/month):
- White-label your chat widget by removing Drahft branding
- Great for maintaining a fully branded client experience
- Note: Professional plan includes white-label branding at no extra cost

Add-ons can be managed from the Billing page.`,
      },
      {
        question: 'Can I upgrade or downgrade my plan anytime?',
        answer: `Absolutely! You can change your plan at any time from the Billing page in your dashboard.

When upgrading:
- You get immediate access to the new plan's features
- Your usage limits increase right away
- You will be charged the prorated difference for the remaining billing period

When downgrading:
- Your current plan features remain active until the end of your billing period
- At renewal, you will switch to the lower plan
- Any features exclusive to the higher plan will no longer be available

Yearly billing saves 20% compared to monthly, and you can switch between billing intervals.`,
      },
      {
        question: 'What is the rate limit and how does it affect me?',
        answer: `Rate limits control how many AI requests your account can make per minute. This ensures consistent performance for all users.

Rate limits by plan:
- Hobby: 10 requests/minute
- Essentials: 20 requests/minute
- Professional: 40 requests/minute

In practice, these limits are generous for normal usage. A rate limit of 10 per minute means your widget can handle multiple simultaneous visitors without issue. Higher-tier plans are designed for busier businesses with more traffic.

If you hit the rate limit, requests are briefly queued and processed as capacity becomes available. Your visitors will not see an error, just a slightly longer response time.`,
      },
    ],
  },
  {
    title: 'Widget & Integrations',
    items: [
      {
        question: 'How do I install the widget on my website?',
        answer: `Installing the Drahft widget is quick and simple:

1. Go to Widget Settings in your dashboard
2. Click "Copy Code" to copy your embed snippet
3. Paste the code into your website before the closing </body> tag
4. Save and publish your site

For WordPress: We provide a free plugin! Download it from the Integrations page, install it via Plugins > Add New > Upload, then paste your widget snippet in Settings > Drahft.

For other platforms: Visit the Integrations page for step-by-step guides for 40+ platforms including Wix, Squarespace, Shopify, Webflow, Showit, and more.

Your widget will appear automatically after installation. If you have caching enabled, clear your cache to see it right away.`,
      },
      {
        question: 'Can I use the widget on multiple websites?',
        answer: `Not currently. Multi-site Support is not part of the current plans.

If you need a multi-site setup, contact support and we can help you plan the best current approach for your account.`,
      },
      {
        question: 'How do I customize the look of my widget?',
        answer: `Widget Customization is available on Essentials and above. Go to Widget Settings in your dashboard to change:
- Header background and text colors
- Message bubble colors
- Assistant name and avatar image
- Chat bubble icon
- Greeting message and pre-message text
- Pre-message delay (how long before the greeting appears)
- Widget position (bottom-right or bottom-left)
- Custom CSS for advanced styling

All changes are previewed in real time before you save. Hobby plan users get a clean, professional default design.`,
      },
      {
        question: 'What is white-label branding?',
        answer: `White-label branding removes the "Powered by Drahft" text from the bottom of your chat widget. This gives your clients a fully branded experience with no reference to Drahft.

How to get it:
- Professional plan: Included at no extra cost
- Professional or Essentials plan: Available as a $39/month add-on
- Hobby plan: Not available

You can enable the add-on from the Billing page in your dashboard. The branding will be removed automatically after purchase.`,
      },
      {
        question: 'What channels will Drahft support besides the website widget?',
        answer: `We are actively working on additional messaging channels:
- Facebook Messenger (coming soon): Connect your Facebook Page to respond to Messenger inquiries through Drahft
- Instagram DM (coming soon): Manage Instagram direct messages through your dashboard

When these channels launch, conversations from all sources will appear in your unified Conversations dashboard. Stay tuned to our Changelog for updates!`,
      },
      {
        question: 'Does Drahft sync with Gmail? Is there an extra cost?',
        answer: `Yes - Gmail sync is automatic and there is no extra cost to you.

When you connect your Google account from the Integrations page, Drahft requests permission to read and send Gmail on your behalf. Here is what that means:
- Automatic inbox sync: Drahft periodically polls your Gmail inbox and pulls incoming client emails into your Unified Conversations view. You do not need to do anything, it happens in the background.
- Send replies: You can reply to emails directly from Drahft without switching to Gmail.
- No extra billing: The Gmail API is free. Google does not charge per API call for Gmail. Your only costs remain your Drahft subscription and your existing infrastructure.

Your Gmail data is fetched in real-time and is not permanently stored in our database. See our Privacy Policy for full details on what we access and why.`,
      },
      {
        question: 'What Google permissions does Drahft request and why?',
        answer: `When you click "Connect Google Calendar" on the Integrations page, Drahft requests three Google permissions in a single consent step:

- Calendar (read/write): Used to create booking events, check your availability, and display upcoming appointments in your dashboard.
- Gmail Read: Used to pull incoming client emails into your Unified Conversations inbox.
- Gmail Send: Used so you can reply to client emails directly from Drahft.

All three are requested together because they share a single OAuth connection. You can revoke access at any time from your Integrations page or from your Google Account at myaccount.google.com/permissions.

We never sell or share your Google data. See our Privacy Policy for the full Google API Limited Use disclosure.`,
      },
    ],
  },
  {
    title: 'Lead Deletion & Inbox Cleanup',
    items: [
      {
        question: 'Why did a deleted email lead or conversation come back?',
        answer: `If the original email still exists in your connected inbox, Drahft may see that message again during email sync and rebuild the lead from the sender's email address.

This usually happens when:
- The lead was created from an inbound email
- The conversation or project was deleted in Drahft only
- The original Gmail or IMAP message still exists and is picked up again by sync

The current behavior is designed to avoid permanently blocking that person forever. If the same lead sends a brand new email later, Drahft should still be able to create a fresh lead for that new inquiry.`,
      },
      {
        question: 'What is the best way to delete a lead without permanently blocking them?',
        answer: `Use this order:

1. Delete the contact, conversation, or project inside Drahft
2. If the lead was already reappearing before the latest fix, delete it one more time after your workspace is updated
3. Only delete or archive the thread in Gmail if you want that old message removed from the source inbox too

Important notes:
- Deleting in Drahft should now be enough for old emails that were already received before deletion
- You do not need to permanently block the lead just to remove old history
- If that person sends a brand new email later, Drahft can still create a new lead from the new message
- If you want the thread gone from every system, clean it up in Gmail or your email provider as well`,
      },
      {
        question: 'When should I also delete the email from Gmail?',
        answer: `Delete or archive the source email when you want a full cleanup across every system, not just inside Drahft.

That is the best option when:
- You never want that old thread visible in Gmail again
- You are cleaning up spam, test leads, or duplicate inquiries
- You want to reduce the chance of old mailbox history being re-imported after reconnecting email

If you only want to remove the lead from Drahft and still keep the original email in Gmail for your records, deleting inside Drahft is the normal workflow.`,
      },
    ],
  },
  {
    title: 'Data, Analytics & Exports',
    items: [
      {
        question: 'How does contact and lead management work?',
        answer: `Every chat interaction automatically captures lead information. When a visitor shares their name, email, or phone number during a conversation, Drahft saves it to your Contacts page.

Key features:
- Unlimited contact storage on all plans
- Automatic lead capture from conversations
- Search and filter your contacts
- View conversation history for each contact
- Export contacts to CSV for use in other tools

This means you never lose a lead, even if they chat at 2 AM while you are asleep.`,
      },
      {
        question: 'Can I export my data?',
        answer: `Yes. Chat and Data Exports are available on Essentials and above. You can export:
- Conversation transcripts
- Contact and lead information
- Booking data

      You can also create a full account backup from Account Settings > Backup by clicking "Backup & Export." This downloads a JSON backup file with the date and time in the filename.

Exports are available in CSV format, making it easy to import into other tools like Excel, Google Sheets, or your CRM.

This is great for keeping backups, sharing data with your team, or analyzing trends over time.`,
      },
            {
        question: 'How do backup restore points work?',
        answer: `In Account Settings > Backup, Drahft keeps your latest 5 restore points so you can roll back quickly.

      How to use it:
      - Click "Backup & Export" anytime to create a fresh restore point and download a backup file
      - Use "Import Backup" to restore from a previously exported JSON file
      - Use "Restore" next to any restore point to roll back your account state

      New backups automatically rotate older ones after the latest 5 are retained.`,
            },
            {
        question: 'Can I undo a mistaken restore?',
        answer: `Yes. Before every restore, Drahft automatically creates a pre-restore snapshot.

      That means if you restore the wrong backup, you can click "Undo Last Restore" in Account Settings > Backup to return to the exact state from right before that restore.

      Important:
      - Restoring overwrites current account data
      - For extra safety, create a fresh backup before importing or restoring older data`,
            },
      {
        question: 'Does Drahft support Google Analytics?',
        answer: `Google Analytics integration is available on Professional plan. It allows you to track:
- Widget interactions and engagement
- Conversion rates from chat to booking
- User behavior and traffic patterns
- Which pages generate the most conversations

This helps you understand how your AI assistant contributes to your business and where to focus your marketing efforts.`,
      },
      {
        question: 'What is Link Tracking?',
        answer: `Link Tracking (Professional) lets you track which marketing links drive the most inquiries to your AI assistant.

This is useful for:
- Measuring the effectiveness of social media campaigns
- Tracking which pages convert the most visitors into leads
- Understanding where your best clients come from

The feature also includes magic links that allow returning clients to continue previous conversations without logging in.`,
      },
    ],
  },
  {
    title: 'Teams & Collaboration',
    items: [
      {
        question: 'Can I add team members to my account?',
        answer: `Yes! Team member access depends on your plan:
- Hobby, Essentials, and Professional: Single user only (no team features)
- Enterprise: Custom team sizes available

Team members can access the dashboard, view conversations, manage bookings, and collaborate on client communication. Each team member gets their own login credentials.`,
      },
      {
        question: 'How do I create a team?',
        answer: `To create a team, go to the Teams page in your dashboard.

Enterprise:
- Click "Create Team" and name your team
- Your team is created instantly with you as the owner
Hobby, Essentials, and Professional:
- Team features are not available on these plans. Upgrade to Professional to collaborate with your team.`,
      },
      {
        question: 'How do I invite someone to my team?',
        answer: `Once your team is created:
1. Go to the Teams page in your dashboard
2. Click the "Invite Member" button
3. Enter the email address of the person you want to invite
4. Click "Send Invite"

The invited person will receive an email with a link to join your team. They will need to create a Drahft account (or log in with their existing one) to accept the invite.

Pending invites are visible on your Teams page. You can cancel an invite before it is accepted if needed.`,
      },
      {
        question: 'What happens when someone accepts a team invite?',
        answer: `When an invited person clicks the link in their email:
1. They will be taken to a Join Team page
2. If they do not have an account, they can create one
3. If they are already logged in, they will see the team details and can click "Join Team"
4. Once they join, they appear as a team member on your Teams page

Team members share your plan's usage pool (AI messages, email drafts, etc.), so usage is tracked across the entire team.`,
      },
      {
        question: 'How many team members can I have?',
        answer: `Team member limits depend on your plan:

Hobby, Essentials, and Professional:
- 1 user only (no team features)

Enterprise:
- Custom team sizes available

If you need more members, contact us about Enterprise options.`,
      },
      {
        question: 'How does shared usage work with teams?',
        answer: `When you have a team, all members share your plan's usage pool:
- AI message credits are shared across the team
- Email draft quotas are shared
- Booking limits are shared
- Training data limits apply to the team as a whole

For example, if your Professional plan includes unlimited AI messages, all team members benefit from that. Usage is tracked per member so you can see who is using what, but the limits apply to the team total.

This means you do not need separate plans for each team member. Your Professional plan covers everyone on the team.`,
      },
      {
        question: 'Can I remove a team member?',
        answer: `Yes. As the team owner, you can remove any team member:
1. Go to the Teams page in your dashboard
2. Find the member you want to remove
3. Click the remove or "kick" button next to their name
4. Confirm the removal

The removed member will immediately lose access to your team's dashboard, conversations, and data. Their personal Drahft account will remain intact if they have one.

Note: Only the team owner can remove members. Team members cannot remove each other or the owner.`,
      },
      {
        question: 'Can I leave a team I joined?',
        answer: `Yes. If you are a team member (not the owner), you can leave a team at any time:
1. Go to the Teams page
2. Click "Leave Team"
3. Confirm your decision

After leaving, you will no longer have access to the team's shared dashboard, conversations, or data. If you want to rejoin later, the team owner will need to send you a new invite.

Note: Team owners cannot leave their own team. If you are the owner and want to disband the team, you will need to remove all members first and then delete the team.`,
      },
      {
        question: 'What roles exist within a team?',
        answer: `Teams currently support two roles:

Owner:
- The person who created the team
- Full access to all team settings and management
- Can invite and remove team members
- Can rename or delete the team
- Manages billing

Member:
- Invited by the owner
- Can access the shared dashboard, conversations, bookings, and contacts
- Can use AI features within the team's usage pool
- Cannot manage team settings, invite others, or remove members

More granular role options (editor, viewer, etc.) may be added in future updates.`,
      },
      {
        question: 'Does adding team members affect my billing?',
        answer: `Professional plan:
- 5 team members are included at no extra cost
- No billing impact when you invite members

All other plans (Hobby, Essentials, Professional):
- Team features are not available. Upgrade to Professional to access team collaboration.

Team members share your Professional plan's features and usage pool at no additional charge.`,
      },
      {
        question: 'What is VIP Onboarding?',
        answer: `VIP Onboarding is a personalized setup experience included with the Professional plan. You will get a one-on-one call with our team to:
- Configure your AI assistant for optimal performance
- Customize prompts to match your brand voice
- Set up integrations (Google Calendar, analytics, etc.)
- Optimize your training data for the best responses
- Walk through advanced features and best practices

It is like having a dedicated assistant to make sure everything is perfect before you go live.`,
      },
    ],
  },
  {
    title: 'Technical Questions',
    items: [
      {
        question: 'Where is my data stored and is it secure?',
        answer: `Your data is handled with care and protected using industry-standard security practices. All information is stored on secure servers with strong encryption both in transit and at rest.

Here are a few key points:
- Your data is kept private and isolated to your account
- It is never used to train other AI models
- You can delete your data at any time
- Our systems follow modern compliance standards, including SOC 2 and GDPR-aligned handling

For full details, you can review our Privacy Policy at any time.`,
      },
      {
        question: 'How does the AI rate limiting work?',
        answer: `To ensure consistent quality for all users, each plan has an AI request rate limit (requests per minute). This prevents any single account from overwhelming the system.

Rate limits:
- Hobby: 10/min
- Essentials: 20/min
- Professional: 40/min

Additionally, "Unlimited" message plans are subject to a fair-use policy of approximately 500 actions per day. This is designed to prevent automated abuse, not to limit normal business usage. Regular business activity will never come close to this limit.

If you occasionally hit the rate limit during a traffic spike, requests queue briefly and process as capacity becomes available.`,
      },
      {
        question: 'How does the widget load on my site?',
        answer: `The Drahft widget is designed to be lightweight and fast:
- It loads asynchronously, so it never slows down your website
- The script is served from a CDN (Content Delivery Network) for fast global delivery
- It initializes only after your page has loaded
- The widget does not affect your site's SEO or page speed score

The embed code is a small JavaScript snippet (under 1KB) that fetches the full widget only when needed. Your site's performance will not be impacted.`,
      },
      {
        question: 'Can I integrate Drahft with my existing website?',
        answer: `Yes, you can. Drahft works with virtually any website platform, and adding it takes just a few minutes. We support 40+ platforms with step-by-step guides in the Integrations page.

For WordPress users, we offer a free plugin that makes installation even easier. Just download, install, paste your widget snippet, and you are live.

Once installed, the widget blends naturally into your existing design and starts helping your visitors right away.`,
      },
    ],
  },
  {
    title: 'Troubleshooting',
    items: [
      {
        question: "My AI Agent isn't responding correctly. What can I do?",
        answer: `If your AI Agent is not responding the way you expect, here are a few simple steps that usually help:
- Review your training data and make sure everything is clear and easy to understand
- Add more details about the topics your clients ask about most
- Remove any outdated or confusing information
- Check your training data character limit (it depends on your plan)
- If you are using an advanced custom prompt (Professional), double-check that the instructions are written clearly and do not conflict with your training data

A few small adjustments often make a big difference in how accurately Drahft responds.`,
      },
      {
        question: 'The chat widget is not appearing on my website',
        answer: `If your chat widget is not showing up, here are a few common things to check:

1. Verify your embed code
- Make sure the full script was copied and pasted correctly
- Confirm your AI Agent is enabled in Widget Settings
- Check that your license key is active and correctly applied

2. Check your website placement
- The script should be placed before the closing </body> tag
- Look for any JavaScript errors in your browser's developer console
- Test your site on different browsers or devices

3. Clear cache
- Refresh your browser after clearing the cache
- If your site uses caching plugins (like WP Super Cache or W3 Total Cache), clear those as well
- Try viewing your site in an incognito or private window

4. Check for content blockers
- Ad blockers or privacy extensions may prevent the widget from loading
- Try disabling them temporarily to test

5. WordPress-specific
- If using the Drahft WordPress plugin, verify the snippet is saved in Settings > Drahft
- Check if your theme supports custom scripts

If you still do not see the widget after these steps, our support team can help you troubleshoot the integration.`,
      },
      {
        question: 'My bookings are not syncing to Google Calendar',
        answer: `If bookings created through Drahft are not appearing in your Google Calendar:

1. Check your plan setup: Google Calendar sync is available on all plans
2. Verify the connection: Go to Integrations > Google Calendar and check that it shows "Connected"
3. Re-authorize if needed: Sometimes the OAuth token expires. Disconnect and reconnect your Google Calendar
4. Check your working hours: Make sure your availability settings match your expectations
5. Review the booking: Open the booking in your dashboard and check its status (pending, approved, etc.)

If the issue persists, try disconnecting and reconnecting your Google Calendar from the Integrations page.`,
      },
      {
        question: 'My Gmail emails are not appearing in Conversations',
        answer: `If your Gmail inbox is not syncing into the Conversations view, work through these steps:

1. Check your connection: Go to Integrations and confirm Google shows as "Connected". If not, click Connect and re-authorize.
2. Confirm you granted Gmail permissions: During the Google OAuth flow, Drahft requests Calendar and Gmail access together. If you approved Calendar only, disconnect and reconnect to grant all permissions.
3. Wait for the next sync: Gmail syncs on a scheduled background job every few minutes. New emails may take a short while to appear after first connecting.
4. Check your Gmail filters: Emails automatically archived or filtered to skip the inbox may not appear in Drahft.
5. Still not working: Contact support@drahft.com with your account email so we can check the server sync logs.
`,
      },
      {
        question: 'I connected Google but Gmail shows an API error',
        answer: `This means the Gmail API was not enabled in the underlying Google Cloud project. This is a configuration issue on our side, please contact support@drahft.com and we will resolve it promptly.

In the meantime:
- Your Google Calendar sync continues to work normally
- Bookings, availability checks, and calendar events are unaffected
- Only the Gmail email-polling feature is impacted until the API is enabled

Once resolved, Gmail will sync automatically without any action needed from you.`,
      },
      {
        question: 'I hit my monthly booking limit. What can I do?',
        answer: `If you have reached your monthly booking limit:
- Hobby plan: 5 bookings/month limit
- Essentials plan: 15 bookings/month limit
- Professional: Unlimited bookings

Your options:
- Wait for the limit to reset on the 1st of next month
- Upgrade to a higher plan for more (or unlimited) bookings

Once the limit is reached, the AI will politely let visitors know that booking slots are currently full and suggest they reach out directly via email or phone.`,
      },
      {
        question: 'I am getting a rate limit error. What does that mean?',
        answer: `Rate limit errors occur when your AI widget receives more requests per minute than your plan allows.

Each plan has a rate limit:
- Hobby: 10 requests/minute
- Essentials: 20 requests/minute
- Professional: 40 requests/minute

If you are seeing rate limit warnings frequently:
- Check if you have unusually high traffic (good problem to have!)
- Consider upgrading to a plan with higher limits
- The system will briefly queue requests and process them as capacity becomes available, so most visitors will not notice

Rate limits protect the quality of service for all users. In normal usage, they are rarely hit.`,
      },
      {
        question: 'My team invite link is not working',
        answer: `If a team invite link is not working for the invited person, check these common issues:

1. Expired invite: Team invites expire after a set period. Check the Teams page to see if the invite is still pending. If expired, send a new one
2. Already accepted: The invite can only be used once. If it was already accepted, the person should be a member already
3. Wrong email: The invite is tied to a specific email address. Make sure the person is logging in with the exact email the invite was sent to
4. Account required: The invited person must create a Drahft account (or log in to an existing one) before they can accept the invite
5. Already on another team: A user can only belong to one team at a time. If they are already on another team, they will need to leave it first

You can cancel the old invite from the Teams page and send a fresh one if needed.`,
      },
      {
        question: 'I cannot see the Teams page or team features',
        answer: `The Teams page and team features are available on the Enterprise plan.

      - Hobby, Essentials, and Professional: You will see the Teams page but with a prompt to upgrade to Enterprise
      - Enterprise: Full team features are available immediately

      If you are on Enterprise and still cannot access team features:
1. Check your subscription status on the Billing page
2. Make sure your plan is active (not expired or canceled)
3. Try logging out and back in
4. Contact support if the issue persists`,
      },
      {
        question: 'A team member left but their usage is still showing',
        answer: `When a team member leaves or is removed, their accumulated usage for the current billing period remains in the usage totals. This is expected behavior because the usage already occurred.

Usage counters reset on the 1st of every month, at which point the departed member's usage will no longer be included.

If you believe the usage numbers are incorrect or not updating after the next reset, please contact support.`,
      },
    ],
  },
];